import { useEffect, useState } from "react";
import { AlertCircle, Briefcase, Calendar, CreditCard, Users } from "lucide-react";
import { fetchAdminMetrics } from "@/lib/admin";

const money = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });

export default function AdminDashboard() {
  const [metrics, setMetrics] = useState<Awaited<ReturnType<typeof fetchAdminMetrics>> | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let mounted = true;

    void fetchAdminMetrics()
      .then((result) => {
        if (mounted) {
          setMetrics(result);
          setError("");
        }
      })
      .catch((nextError) => {
        if (mounted) {
          setError(nextError.message ?? "Unable to load admin metrics.");
        }
      });

    return () => {
      mounted = false;
    };
  }, []);

  const cards = [
    { label: "Businesses", value: metrics?.businessCount ?? 0, detail: `${metrics?.activeBusinessCount ?? 0} active`, icon: Briefcase },
    { label: "Users", value: metrics?.userCount ?? 0, detail: `${metrics?.activeUserCount ?? 0} active`, icon: Users },
    { label: "Bookings", value: metrics?.bookingCount ?? 0, detail: "Across the full platform", icon: Calendar },
    { label: "MRR", value: money.format((metrics?.monthlyRecurringRevenueCents ?? 0) / 100), detail: `${metrics?.activeSubscriptionCount ?? 0} active subscriptions`, icon: CreditCard },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-heading font-bold text-slate-950">Admin Dashboard</h1>
        <p className="mt-2 max-w-2xl text-sm text-slate-600">Monitor businesses, users, bookings, and subscription health across the Schedora platform.</p>
      </div>

      {error && (
        <div className="flex items-start gap-3 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          <AlertCircle className="mt-0.5 h-4 w-4" />
          <p>{error}</p>
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {cards.map((card) => (
          <section key={card.label} className="rounded-2xl border border-black/5 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-slate-500">{card.label}</p>
              <div className="rounded-full bg-slate-100 p-2 text-slate-700">
                <card.icon className="h-4 w-4" />
              </div>
            </div>
            <p className="mt-6 text-3xl font-semibold text-slate-950">{card.value}</p>
            <p className="mt-2 text-sm text-slate-500">{card.detail}</p>
          </section>
        ))}
      </div>

      <section className="rounded-2xl border border-black/5 bg-white p-6 shadow-sm">
        <h2 className="text-lg font-semibold text-slate-950">What this admin panel covers</h2>
        <div className="mt-4 grid gap-3 md:grid-cols-2">
          <div className="rounded-xl bg-slate-50 p-4 text-sm text-slate-600">Platform-wide business visibility with activation controls and subscription status.</div>
          <div className="rounded-xl bg-slate-50 p-4 text-sm text-slate-600">User directory with tenant filtering and role-aware activity state.</div>
          <div className="rounded-xl bg-slate-50 p-4 text-sm text-slate-600">Bookings audit across all tenants using the existing appointments model.</div>
          <div className="rounded-xl bg-slate-50 p-4 text-sm text-slate-600">Supabase RLS enforcement so normal owners and clients cannot read admin datasets.</div>
        </div>
      </section>
    </div>
  );
}
